import {
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonImg,
  IonPage,
  IonText,
  IonFooter,
  IonToolbar,
  IonItem,
  IonInput,
  IonButton,
  IonIcon,
  useIonAlert,
  useIonLoading,
  useIonToast,
} from '@ionic/react';
import React, { useState, useCallback, useMemo } from 'react';
import {
  personCircle,
  eye,
  eyeOff,
  businessOutline,
  keyOutline,
  wifiOutline,
  syncSharp,
  happyOutline,
  warningOutline,
} from 'ionicons/icons';
import { Device } from '@capacitor/device';
import { RestApi } from '../services/Api';
import { useAuth } from '../data/sessions/Auth';
import { UserAction } from '../data/state/UserReducer';
import { useDispatch } from 'react-redux';
import { Preferences } from '@capacitor/preferences';
import { Browser } from '@capacitor/browser';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Directory, DownloadFileResult, Filesystem } from '@capacitor/filesystem';
import { FileOpener, FileOpenerOptions } from '@capacitor-community/file-opener';
import { ApiResponse } from '../models/Data';
import './Login.scss';

interface LoginFormData {
  email: string;
  password: string;
  port: string;
}

const Login: React.FC = () => {
  const { logIn } = useAuth()!;
  const [presentToast] = useIonToast();
  const dispatch = useDispatch();
  const [alert] = useIonAlert();
  const { t } = useTranslation(["general"]);
  const [presentLoading, dismissLoading] = useIonLoading();

  // Form state
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    port: window.localStorage.getItem("port") ?? "",
  });
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [selectedUrl, setSelectedUrl] = useState<string>(
    process.env.REACT_APP_URL_MAIN || "",
  );
  const [portState, setPortState] = useState({
    main: false,
    backup: false,
  });

  // Environment variables - useMemo to avoid changing reference on each render
  const ENV = useMemo(
    () => ({
      URL_MAIN: process.env.REACT_APP_URL_MAIN || "",
      URL_BACKUP: process.env.REACT_APP_URL_BACKUP || "",
      VERSION: process.env.REACT_APP_VERSION || "",
    }),
    [],
  );

  // Helper: Parse error message from API response
  const parseErrorMessage = useCallback((message: string): string => {
    try {
      const messageObj = JSON.parse(message);
      const errors: string[] = [];

      for (const key in messageObj) {
        if (!messageObj.hasOwnProperty(key)) continue;
        const obj = messageObj[key];
        for (const prop in obj) {
          if (!obj.hasOwnProperty(prop)) continue;
          errors.push(`${key}: ${obj[prop]}`);
        }
      }
      return errors.join("\n");
    } catch {
      return message;
    }
  }, []);

  // Helper: Show alert message
  const showAlert = useCallback(
    async (status: string, body: string) => {
      await alert({
        header: t(status),
        message: body,
        cssClass: `header-alert-${status}`,
        buttons: [{ cssClass: "alert-button-confirm", text: "Ok" }],
      });
    },
    [alert, t],
  );

  // Helper: Show toast message
  const showToast = useCallback(
    (message: string, icon?: string) => {
      presentToast({
        message,
        duration: 1500,
        icon: icon || warningOutline,
      });
    },
    [presentToast],
  );

  // Helper: Get error message from exception
  const getErrorMessage = useCallback((error: any): string => {
    if (
      error.message ===
      "Failed to execute 'open' on 'XMLHttpRequest': Invalid URL"
    ) {
      return "Maaf Tidak Ada ID Company";
    }
    return error.message || "Terjadi kesalahan";
  }, []);

  // Helper: Update form field
  const updateField = useCallback(
    (field: keyof LoginFormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Helper: Open file with specific type
  const openFileWithType = useCallback(
    async (filePath: string, fileType: string) => {
      const fileOpenerOptions: FileOpenerOptions = {
        filePath,
        contentType: fileType,
      };

      try {
        await FileOpener.open(fileOpenerOptions);
      } catch (error: any) {
        await showAlert("error", error.message);
      }
    },
    [showAlert],
  );

  // Handle login submission
  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const { email, password, port } = formData;

      // Validation
      if (!email || !password || !port) {
        showToast(t("required"));
        return;
      }

      try {
        const deviceInfo = await Device.getId();
        // await Preferences.set({ key: 'url', value: 'https://gss-web.test' });
        await Preferences.set({ key: "url", value: selectedUrl + port });

        await presentLoading({ message: t("loading") });

        const response = (await RestApi("login", "post", {
          email,
          password,
          imei: deviceInfo.identifier,
        })) as ApiResponse;

        if (response.data.status) {
          // Save user data
          dispatch(UserAction.setProfile(response.data.data));
          await Preferences.set({
            key: "token",
            value: response.data.token || "",
          });
          await Preferences.set({
            key: "auth",
            value: response.data.data.user_id,
          });

          showToast(`Hello ${response.data.data.user_name}`, happyOutline);
          logIn(response.data.data);
        } else {
          const errorMessage = parseErrorMessage(response.data.message);
          await showAlert("error", errorMessage);
        }
      } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        await showAlert("error", errorMessage);
      } finally {
        dismissLoading();
      }
    },
    [
      formData,
      selectedUrl,
      t,
      presentLoading,
      dismissLoading,
      dispatch,
      logIn,
      showToast,
      showAlert,
      parseErrorMessage,
      getErrorMessage,
    ],
  );

  // Handle URL selection (Main/Backup)
  const handleUrlSelect = useCallback(() => {
    alert({
      header: "Pilih Koneksi",
      cssClass: "header-alert",
      inputs: [
        {
          label: "Utama",
          type: "radio",
          value: ENV.URL_MAIN,
          checked: selectedUrl === ENV.URL_MAIN,
        },
        {
          label: "Backup",
          type: "radio",
          value: ENV.URL_BACKUP,
          checked: selectedUrl === ENV.URL_BACKUP,
        },
      ],
      buttons: [
        {
          text: t("yes"),
          cssClass: "alert-button-confirm",
          role: "confirm",
          handler: (data: string) => {
            if (!data) return;
            setSelectedUrl(data);
            if (data === ENV.URL_BACKUP) {
              if (!portState.main) {
                updateField("port", String(Number(formData.port)));
                setPortState({ main: true, backup: false });
              } else {
                setPortState((prev) => ({ ...prev, backup: false }));
              }
            } else {
              if (!portState.backup) {
                updateField("port", String(Number(formData.port)));
                setPortState({ main: false, backup: true });
              } else {
                setPortState((prev) => ({ ...prev, main: false }));
              }
            }
          },
        },
      ],
    });
  }, [selectedUrl, ENV, portState, formData.port, t, alert, updateField]);

  // Handle forgot password
  const handleForgotPassword = useCallback(() => {
    alert({
      header: "Masukan Email",
      cssClass: "header-alert",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
          cssClass: "alert-button-cancel",
        },
        {
          text: t("yes"),
          role: "confirm",
          cssClass: "alert-button-confirm",
          handler: async (data: { forgot_email?: string }) => {
            if (!formData.port || !data.forgot_email) {
              showToast(t("required"));
              return;
            }

            try {
              await Preferences.set({
                key: "url",
                value: selectedUrl + formData.port,
              });
              await presentLoading({ message: t("loading") });

              const response = (await RestApi("forgot", "post", {
                email: data.forgot_email,
              })) as ApiResponse;

              if (response.data.status) {
                await showAlert("success", response.data.message);
              } else {
                const errorMessage = parseErrorMessage(response.data.message);
                await showAlert("error", errorMessage);
              }
            } catch (error: any) {
              const errorMessage = getErrorMessage(error);
              await showAlert("error", errorMessage);
            } finally {
              dismissLoading();
            }
          },
        },
      ],
      inputs: [
        {
          placeholder: "Email",
          type: "email",
          name: "forgot_email",
        },
        {
          placeholder: "ID Company",
          type: "number",
          value: formData.port,
          name: "forgot_port",
        },
      ],
    });
  }, [
    formData.port,
    selectedUrl,
    t,
    alert,
    presentLoading,
    dismissLoading,
    showToast,
    showAlert,
    parseErrorMessage,
    getErrorMessage,
  ]);

  // Handle app update check
  const handleCheckUpdate = useCallback(async () => {
    if (!formData.port) {
      showToast(t("required") + " ID Company");
      return;
    }

    try {
      // await Preferences.set({ key: 'url', value: 'https://gss-web.test' });
      await Preferences.set({ key: "url", value: selectedUrl + formData.port });
      await presentLoading({ message: t("loading") });

      const response = (await RestApi(
        `apk?version=${ENV.VERSION}`,
        "get",
        {},
      )) as ApiResponse;

      if (response.data.status) {
        if (Capacitor.isNativePlatform()) {
          Filesystem.downloadFile({
            url: response.data.message,
            path: `cos-V${response.data.message.replace(/[^0-9]/g, "")}.apk`,
            directory: Directory.Documents,
          }).then((res: DownloadFileResult) => {
            showAlert(
              "success",
              "Silahkan cek folder document penyimpanan HP anda untuk menginstall secara manual versi terbaru",
            );
            if (res.path) {
              openFileWithType(
                res.path,
                "application/vnd.android.package-archive",
              );
            }
            Browser.open({ url: response.data.message });
          });
        }
        dismissLoading();
      } else {
        await showAlert("success", response.data.message);
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      await showAlert("error", errorMessage);
    } finally {
      dismissLoading();
    }
  }, [
    formData.port,
    ENV.VERSION,
    t,
    presentLoading,
    dismissLoading,
    showToast,
    showAlert,
    getErrorMessage,
    openFileWithType,
    selectedUrl,
  ]);

  return (
    <IonPage>
      <IonContent fullscreen className="login-content">
        <IonGrid className="container-login ion-padding">
          <IonRow className="ion-no-padding">
            <IonCol size="12" className="ion-no-padding">
              {/* Logo */}
              <IonText className="ion-text-center">
                <IonImg
                  className="logo"
                  src="assets/img/logo-gforce.png"
                  alt="Logo"
                />
              </IonText>

              {/* Login Form */}
              <form onSubmit={handleLogin}>
                {/* Username Input */}
                <IonItem>
                  <IonIcon
                    color="light"
                    slot="start"
                    size="small"
                    icon={personCircle}
                  />
                  <IonInput
                    color="light"
                    type="text"
                    placeholder="Username"
                    value={formData.email}
                    onIonInput={(e) => updateField("email", e.detail.value!)}
                    required
                  />
                </IonItem>

                {/* Password Input */}
                <IonItem>
                  <IonIcon
                    color="light"
                    slot="start"
                    size="small"
                    icon={keyOutline}
                  />
                  <IonInput
                    color="light"
                    type={showPassword ? "password" : "text"}
                    placeholder="Password"
                    value={formData.password}
                    onIonInput={(e) => updateField("password", e.detail.value!)}
                    required
                  />
                  <IonIcon
                    color="light"
                    slot="end"
                    size="small"
                    icon={showPassword ? eyeOff : eye}
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: "pointer" }}
                  />
                </IonItem>

                {/* Company ID Input */}
                <IonItem>
                  <IonIcon
                    color="light"
                    slot="start"
                    size="small"
                    icon={businessOutline}
                  />
                  <IonInput
                    color="light"
                    type="number"
                    placeholder="ID Company"
                    value={formData.port}
                    onIonInput={(e) => updateField("port", e.detail.value!)}
                    required
                  />
                </IonItem>

                {/* Forgot Password Link */}
                <IonText
                  color="light"
                  className="ion-float-right ion-padding"
                  onClick={handleForgotPassword}
                  style={{ cursor: "pointer" }}
                >
                  Lupa Password ?
                </IonText>

                {/* Login Button */}
                <IonButton expand="block" type="submit" className="ion-padding">
                  Login
                </IonButton>
              </form>
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Footer Actions */}
        <IonFooter className="ion-no-border">
          <IonToolbar className="login-footer">
            <IonGrid className="ion-no-padding ion-no-margin">
              <IonRow className="ion-justify-content-center">
                <IonCol size="3" className="ion-no-padding" />
                <IonCol size="3">
                  <IonText
                    color="light"
                    className="ion-text-center ion-no-padding"
                    onClick={handleCheckUpdate}
                    style={{ cursor: "pointer" }}
                  >
                    <IonIcon
                      className="ion-no-padding"
                      color="light"
                      size="large"
                      icon={syncSharp}
                    />
                    <p>
                      <strong>Update</strong>
                    </p>
                  </IonText>
                </IonCol>
                <IonCol size="3">
                  <IonText
                    color="light"
                    className="ion-text-center ion-no-padding"
                    onClick={handleUrlSelect}
                    style={{ cursor: "pointer" }}
                  >
                    <IonIcon
                      className="ion-no-padding"
                      color="light"
                      size="large"
                      icon={wifiOutline}
                    />
                    <p>
                      <strong>Koneksi</strong>
                    </p>
                  </IonText>
                </IonCol>
                <IonCol size="3" className="ion-no-padding" />
              </IonRow>
            </IonGrid>
          </IonToolbar>
        </IonFooter>
      </IonContent>
    </IonPage>
  );
};

export default Login;
